import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control, useFieldArray } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';
import { Fab } from '#packages/web/foundations/buttons/fab';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { MessagePage } from '#packages/web/layouts/page/templates/message-page';
import { Message } from '#packages/web/foundations/feedback/message';

export type SiteContactsFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const SiteContactsFormDialog: FC<
  PropsWithChildren<SiteContactsFormDialogProps>
> = ({ open, control, onClose }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'general.siteContacts',
  });

  const deleteContactDialog = useDialog<number>();

  return (
    <SubFormDialog title="Site contacts" open={open} onClose={onClose}>
      <Stack spacing={3} sx={{ pb: 10 }}>
        {fields.length === 0 && (
          <MessagePage
            message={
              <Message>No contacts added yet. Tap + to add one.</Message>
            }
          />
        )}
        {fields.map((field, index) => (
          <Box key={field.id}>
            <Stack spacing={2}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Contact {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="remove contact"
                  onClick={() => deleteContactDialog.open(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    control={control}
                    name={`general.siteContacts.${index}.name`}
                    rules={{ maxLength: 120 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Name"
                          type="text"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    control={control}
                    name={`general.siteContacts.${index}.role`}
                    rules={{ maxLength: 120 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Role"
                          type="text"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    control={control}
                    name={`general.siteContacts.${index}.email`}
                    rules={{ maxLength: 254 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Email"
                          type="email"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    control={control}
                    name={`general.siteContacts.${index}.phone`}
                    rules={{ maxLength: 30 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Phone"
                          type="tel"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    control={control}
                    name={`general.siteContacts.${index}.notes`}
                    rules={{ maxLength: 500 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Notes"
                          type="text"
                          variant="outlined"
                          multiline
                          minRows={2}
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Stack>
            {index < fields.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        ))}
      </Stack>

      <Fab
        tooltip="Add contact"
        onClick={() =>
          append({ name: '', role: '', email: '', phone: '', notes: '' })
        }
      />

      <DeleteConfirmationDialog
        open={deleteContactDialog.isOpen}
        dialogTitle="Remove Contact"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          remove(deleteContactDialog.data!);
          deleteContactDialog.close();
        }}
        onCancel={() => deleteContactDialog.close()}
      />
    </SubFormDialog>
  );
};
